import React, { useEffect, useState } from "react";
import { FiChevronDown, FiX } from "react-icons/fi";
import { sanitizeImageUrl } from "../../utils/sanitizeImageUrl";
import defaultBrandImage from "../../assets/images/default-brand.png";
import {
  appointmentBookedAt,
  appointmentDate,
  appointmentOfficeAddress,
  appointmentTime,
  clientName,
  evaluationId,
  evaluationOf,
  formatAppointmentBookedAt,
  formatAppointmentTime,
  imageGroups,
  trackingNumber,
  vehicleAddressText,
  vehicleBrandImage,
  vehicleFeatures,
  vehicleReg,
  vehicleTitle,
} from "./appointmentHelpers";
import AppointmentStatusBadge from "./AppointmentStatusBadge";

function resolveAppointmentBrandImage(item) {
  return sanitizeImageUrl(vehicleBrandImage(item)) || defaultBrandImage;
}

function DetailSection({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const headingId = `dd-appt-sec-${title.replace(/\s+/g, "-").toLowerCase()}`;

  return (
    <section className={`dd-appt-detail__section${open ? " is-open" : ""}`}>
      <button
        type="button"
        className="dd-appt-detail__toggle"
        aria-expanded={open}
        aria-controls={`${headingId}-body`}
        onClick={() => setOpen((value) => !value)}
      >
        <h3 id={headingId}>{title}</h3>
        <FiChevronDown size={16} aria-hidden />
      </button>
      {open ? (
        <div id={`${headingId}-body`} className="dd-appt-detail__section-body">
          {children}
        </div>
      ) : null}
    </section>
  );
}

function Field({ label, value }) {
  if (value == null || value === "" || value === "—") return null;
  return (
    <div className="dd-drawer__row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function OfficeAppointmentDetail({ item, onClose }) {
  const [preview, setPreview] = useState(null);
  const [brandImage, setBrandImage] = useState(() =>
    resolveAppointmentBrandImage(item)
  );

  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (preview) setPreview(null);
        else onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item, onClose, preview]);

  useEffect(() => {
    setBrandImage(resolveAppointmentBrandImage(item));
  }, [item]);

  if (!item) return null;

  const ev = evaluationOf(item);
  const groups = imageGroups(item);
  const features = vehicleFeatures(item);
  const client = clientName(item);
  const title = vehicleTitle(item);

  return (
    <div
      className="dd-drawer dd-appt-detail"
      role="dialog"
      aria-modal="true"
      aria-label="Appointment details"
    >
      <button
        type="button"
        className="dd-drawer__backdrop"
        aria-label="Close details"
        onClick={onClose}
      />
      <aside className="dd-drawer__panel">
        <header className="dd-drawer__head">
          <div className="dd-appt-detail__head-main">
            <p className="dd-drawer__eyebrow">Appointment</p>
            <div className="dd-appt-detail__title-row">
              <span className="dd-appt-detail__brand" aria-hidden>
                <img
                  src={brandImage}
                  alt=""
                  onError={() => {
                    if (brandImage !== defaultBrandImage) {
                      setBrandImage(defaultBrandImage);
                    }
                  }}
                />
              </span>
              <div className="dd-appt-detail__title-copy">
                <h2>{title}</h2>
                <div className="dd-appt-detail__title-meta">
                  <p className="dd-drawer__reg">
                    {String(vehicleReg(item)).toUpperCase()}
                  </p>
                  <AppointmentStatusBadge item={item} />
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="dd-btn dd-btn--ghost"
            onClick={onClose}
            aria-label="Close"
          >
            <FiX size={18} />
          </button>
        </header>
        <div className="dd-drawer__body">
          <DetailSection key={`${item.id}-booking`} title="Booking" defaultOpen>
            <dl className="dd-drawer__dl">
              <Field label="Client" value={client} />
              <Field label="Username" value={item.username} />
              <div className="dd-drawer__row">
                <dt>Status</dt>
                <dd>
                  <AppointmentStatusBadge item={item} />
                </dd>
              </div>
              <Field
                label="Date"
                value={appointmentDate(item) !== "—" ? appointmentDate(item) : null}
              />
              <Field
                label="Time"
                value={formatAppointmentTime(appointmentTime(item))}
              />
              <Field
                label="Booked"
                value={formatAppointmentBookedAt(appointmentBookedAt(item))}
              />
              <Field label="Office" value={appointmentOfficeAddress(item)} />
              <Field label="Evaluation" value={evaluationId(item)} />
              <Field label="Tracking" value={trackingNumber(item)} />
            </dl>
          </DetailSection>

          <DetailSection key={`${item.id}-vehicle`} title="Vehicle">
            <dl className="dd-drawer__dl">
              <Field label="Brand" value={ev.brand_name} />
              <Field label="Model" value={ev.model_name} />
              <Field label="Variant" value={ev.variant_name} />
              <Field label="Year" value={ev.year_name} />
              <Field label="Fuel" value={ev.fuel_type} />
              <Field label="Mileage" value={ev.Mileage} />
              <Field label="Previous owners" value={ev.Previous_owners} />
              <Field label="Service history" value={ev.Service_history} />
              {ev.car_description ? (
                <Field label="Description" value={ev.car_description} />
              ) : null}
              {ev.car_notes ? <Field label="Notes" value={ev.car_notes} /> : null}
            </dl>
            {features.length ? (
              <dl className="dd-drawer__dl">
                {features.map((row) => (
                  <Field key={row.question} label={row.question} value={row.answer} />
                ))}
              </dl>
            ) : null}
          </DetailSection>

          <DetailSection key={`${item.id}-contact`} title="Contact">
            <dl className="dd-drawer__dl">
              <Field label="Name" value={ev.Website_user || client} />
              <Field label="Email" value={ev.Email} />
              <Field label="Mobile" value={ev.Mobile_number} />
              <Field label="Postcode" value={ev.Postcode} />
              <Field label="Vehicle address" value={vehicleAddressText(item)} />
            </dl>
          </DetailSection>

          {groups.length ? (
            <DetailSection key={`${item.id}-photos`} title="Photos">
              {groups.map((group) => (
                <div key={group.label} className="dd-appt-detail__photos">
                  <h4>{group.label}</h4>
                  <ul>
                    {group.items.map((img) => (
                      <li key={img.id || img.image}>
                        <button
                          type="button"
                          onClick={() => setPreview(img)}
                          title={img.name || group.label}
                        >
                          <img src={img.image} alt={img.name || group.label} />
                          <span>{img.name || group.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </DetailSection>
          ) : null}
        </div>
      </aside>
      {preview ? (
        <div className="dd-appt-detail__lightbox">
          <button
            type="button"
            className="dd-drawer__backdrop"
            aria-label="Close photo"
            onClick={() => setPreview(null)}
          />
          <figure>
            <img src={preview.image} alt={preview.name || "Photo"} />
            <figcaption>{preview.name}</figcaption>
            <button
              type="button"
              className="dd-btn dd-btn--ghost"
              onClick={() => setPreview(null)}
            >
              Close
            </button>
          </figure>
        </div>
      ) : null}
    </div>
  );
}

export default OfficeAppointmentDetail;
